import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import type {
  GraphConfig,
  LangKey,
  PositionPayGapData,
} from "../PaygapPayGradesOverview";

interface UseChartDataProps {
  selectedGraph: GraphConfig | undefined;
  analysisResults: PaygapMetricItemDto[] | null | undefined;
  positionPayGapData: PositionPayGapData[] | undefined;
}

/**
 * Hook to extract and transform chart data based on selected graph configuration
 */
export const useChartData = ({
  selectedGraph,
  analysisResults,
  positionPayGapData,
}: UseChartDataProps): PositionPayGapData[] => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "en";
  const langKey: LangKey =
    language === "cs" ? "cz" : language === "sk" ? "sk" : "en";

  return useMemo(() => {
    // Helper function to get value from analysis results
    const getValue = (key: string): string => {
      if (!analysisResults || analysisResults.length === 0) return "0";
      const item = analysisResults.find((v) => v.item === key);


return item?.value || "0";
    };

    // Helper function to parse numeric values
    const parseValue = (value: string): number => {
      if (
        value === "#DIV/0!" ||
        value === "#VALUE!" ||
        value === "#N/A" ||
        !value ||
        value === "null"
      )
        return 0;
      const parsed = parseFloat(value);


return isNaN(parsed) ? 0 : parsed;
    };

    if (!selectedGraph) {
      // Fallback to positionPayGapData if available
      const dummyData: PositionPayGapData[] = [
        {
          position: "5",
          menAverage: 3800,
          womenAverage: 3750,
          payGap: -1.3,
          description: t("paygap.dummyData.level5"),
          employeeCount: 13637,
        },
        {
          position: "4",
          menAverage: 4800,
          womenAverage: 4600,
          payGap: -4.2,
          description: t("paygap.dummyData.level4"),
          employeeCount: 689,
        },
        {
          position: "3",
          menAverage: 5800,
          womenAverage: 5500,
          payGap: -5.2,
          description: t("paygap.dummyData.level3"),
          employeeCount: 165,
        },
        {
          position: "2",
          menAverage: 7200,
          womenAverage: 6800,
          payGap: -5.6,
          description: t("paygap.dummyData.level2"),
          employeeCount: 71,
        },
        {
          position: "1",
          menAverage: 8500,
          womenAverage: 7800,
          payGap: -8.2,
          description: t("paygap.dummyData.level1"),
          employeeCount: 10,
        },
      ];

      return positionPayGapData &&
        positionPayGapData.some((item) => item.payGap !== 0)
        ? positionPayGapData
        : dummyData;
    }

    // If no analysis results, return empty array (will show fallback)
    if (!analysisResults || analysisResults.length === 0) {
      return [];
    }

    // For seniority_gap graph, extract data from series
    if (selectedGraph.graph_id === "seniority_gap") {
      return selectedGraph.series.map((seriesItem, index) => {
        const value = parseValue(getValue(seriesItem.key));
        const level = index + 1;

        return {
          position: String(level),
          menAverage: 0,
          womenAverage: 0,
          payGap: value * 100,
          description: seriesItem.label[langKey] || seriesItem.label.en,
          employeeCount: 0,
        };
      });
    }

    // For other graphs that use similar bar chart structure
    if (selectedGraph.series && selectedGraph.series.length > 0) {
      return selectedGraph.series.map((seriesItem, index) => {
        let value = parseValue(getValue(seriesItem.key));

        // Special handling for workforce_composition - calculate men percentage as 1 - women percentage
        if (
          selectedGraph.graph_id === "workforce_composition" &&
          seriesItem.key === "male-employees-percentage"
        ) {
          const femaleValue = parseValue(getValue("female-employees-percentage"));

          value = 1 - femaleValue;
        }

        // For percentage-based graphs, keep as percentage; for others, use raw value
        const isPercentage =
          selectedGraph.y_axis[langKey]?.includes("%") ||
          selectedGraph.y_axis.en?.includes("%");
        const displayValue = isPercentage ? value * 100 : value;

        return {
          position: String(index + 1),
          menAverage: 0,
          womenAverage: 0,
          payGap: displayValue,
          description: seriesItem.label[langKey] || seriesItem.label.en,
          employeeCount: 0,
        };
      });
    }

    // Fallback: return empty array
    return [];
  }, [selectedGraph, analysisResults, positionPayGapData, langKey, t]);
};
